/**
 * Оценка аптеки после заказа. Ручек на бэкенде пока нет, поэтому по умолчанию
 * оценки живут в mock-хранилище браузера (`env.reviewsMock`), а заказы,
 * которые ждут оценки, mock выбирает из настоящего списка заказов
 * (`GET /orders`). Когда ручки появятся, переключение — один флаг: список
 * ожидающих даст сервер, экраны и запросы останутся теми же.
 */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient } from '@dorixona/api'
import {
  createHttpMyReviews,
  createMockMyReviews,
  type MyReviewsBackend,
  type ReviewInput,
} from '@dorixona/api/reviews'
import { nowMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { env } from '../../config/env'
import { useOrders } from '../account/api'
import { closedCandidates, toTarget } from './model'

/** Источник оценок: mock в браузере на часах приложения или настоящие ручки. */
export function reviewsBackend(api: ApiClient): MyReviewsBackend {
  return env.reviewsMock
    ? createMockMyReviews({ storage: localStorage, now: nowMs })
    : createHttpMyReviews(api)
}

/**
 * Закрытые заказы, которые ждут оценки, новые первыми. На mock список
 * собирается из заказов покупателя: перечитали заказы — пересчитался и он.
 */
export function usePendingReviews(signedIn: boolean) {
  const api = useApi()
  const mock = env.reviewsMock
  const orders = useOrders(signedIn && mock)
  const closed = useMemo(() => closedCandidates(orders.data ?? []), [orders.data])
  return useQuery({
    queryKey: [...keys.reviewsPending, mock ? closed : 'api'],
    queryFn: async () => (await reviewsBackend(api).pending(closed)).map(toTarget),
    enabled: signedIn && (!mock || orders.isSuccess),
  })
}

/** Оценка одного заказа; null — заказ ещё не оценивали. */
export function useOrderReview(orderId: number, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: keys.orderReview(orderId),
    queryFn: () => reviewsBackend(api).forOrder(orderId),
    enabled: enabled && orderId > 0,
  })
}

export interface SubmitReview {
  orderId: number
  input: ReviewInput
}

/**
 * Оценить заказ. Список ожидающих и оценку заказа перечитываем и после
 * отказа: 409 значит, что заказ уже оценён — и экрану нужна эта оценка.
 */
export function useSubmitReview() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, input }: SubmitReview) =>
      reviewsBackend(api).submit(orderId, { rating: input.rating, comment: input.comment.trim() }),
    onSettled: (_data, _error, { orderId }) =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.reviewsPending }),
        client.invalidateQueries({ queryKey: keys.orderReview(orderId) }),
      ]),
  })
}

/** «Не сейчас»: больше не спрашивать об этом заказе. Оценить его можно из карточки. */
export function useDismissReview() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (orderId: number) => reviewsBackend(api).dismiss(orderId),
    onSuccess: (_data, orderId) =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.reviewsPending }),
        client.invalidateQueries({ queryKey: keys.orderReview(orderId) }),
      ]),
  })
}
